import React from 'react';
import { Navigate, Outlet, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AuthCheckSkeleton } from '@/components/ui/page-skeletons';
import AccessDenied from '@/components/common/AccessDenied';

/**
 * Route guard that requires authentication.
 * Renders Outlet with { user, onLogout } context for child routes.
 */
export const AuthGuard = () => {
  const { isAuthenticated, loading, user, logout } = useAuth();

  if (loading) {
    return <AuthCheckSkeleton />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet context={{ user, onLogout: logout }} />;
};

/**
 * Route guard that redirects authenticated users away (e.g., login page).
 */
export const GuestGuard = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  if (isAuthenticated) {
    // Redirect super admin users to admin panel, regular users to app
    if (user?.internal_role && user.internal_role !== 'none') {
      return <Navigate to="/super-admin" replace />;
    }
    return <Navigate to="/app" replace />;
  }

  return children;
};

/**
 * Route guard for super-admin routes.
 * Requires authentication AND a valid internal_role.
 */
export const SuperAdminGuard = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <AuthCheckSkeleton className="bg-background" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const internalRole = user?.internal_role;
  if (!internalRole || internalRole === 'none') {
    return <Navigate to="/app" replace />;
  }

  return <Outlet />;
};

/**
 * Wrapper that injects { user, onLogout } from Outlet context into the
 * wrapped Component as props. This allows route components to keep their
 * existing `user` / `onLogout` prop API without modification.
 *
 * Optional feature gating: pass `anyOf` (array of permission keys). If the
 * user holds none of them, an Access Denied (403) screen is rendered instead
 * of the page. This is a UX gate only — the backend independently enforces
 * permissions on every data endpoint.
 */
export const WithUser = ({ Component, anyOf, ...extra }) => {
  const ctx = useOutletContext() || {};
  const { hasAnyPermission } = useAuth();

  // Only enforce when we have a concrete permissions array to evaluate. If the
  // payload lacks one (indeterminate), fail open here — the backend independently
  // enforces permissions on every data endpoint, so access is never actually leaked.
  const perms = ctx.user?.permissions;
  const gated = Array.isArray(anyOf) && anyOf.length > 0 && Array.isArray(perms);
  if (gated && !hasAnyPermission(anyOf)) {
    return <AccessDenied />;
  }

  return <Component user={ctx.user} onLogout={ctx.onLogout} {...extra} />;
};

/**
 * Wrapper for public (marketing) pages that injects { isAuthenticated, user }
 * from AuthContext as props.
 */
export const WithPublicAuth = ({ Component, ...extra }) => {
  const { isAuthenticated, user } = useAuth();
  return <Component isAuthenticated={isAuthenticated} user={user} {...extra} />;
};

/**
 * Wrapper for business routes that require the business_profile:manage permission.
 * Renders an Access Denied screen for users without that permission.
 */
export const AdminOnly = ({ Component }) => {
  const ctx = useOutletContext() || {};
  const user = ctx.user;

  const perms = Array.isArray(user?.permissions) ? user.permissions : null;
  const canManageBusinessProfile = perms
    ? perms.includes('business_profile:manage')
    // Fallback for older /auth/me payloads without a permissions array.
    : user && user.role !== 'user';

  if (user && !canManageBusinessProfile) {
    return <AccessDenied />;
  }

  return <Component />;
};
